import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  View,
  FlatList,
  StyleSheet,
  LayoutChangeEvent,
  Platform,
  TouchableOpacity,
  Text,
  Dimensions,
  Image,
  useWindowDimensions,
} from "react-native";
import { fetchShorts } from "../../services/apiService";
import AppLoader from "../../components/AppLoader";
import { useActiveTabStore } from "../../store/activeTabStore";
import { FULL_WIDTH } from "../../shared/constants/dimension";
import { useThemeSettings } from "../../ThemeProvider";
import i18n from "../../localization/i18n";
import { CustomIcon } from "../../components/CustomIcon";
import { useTrackScreenTime } from "../../analytics/useTrackScreenTime";
import { useRoute } from "@react-navigation/native";
import { ROUTES } from "../../shared/constants/routes";
import { useModalStore, ModalType } from "../../store/modalStore";

const SCREEN = Dimensions.get("window");

let YoutubePlayer: any = null;
if (Platform.OS !== "web") {
  YoutubePlayer = require("react-native-youtube-iframe").default;
}

// ---------- Web: iframe Player ----------
function WebShortItem({
  item,
  shouldPlay,
  isPreload,
  width,
  height,
}: {
  item: any;
  shouldPlay: boolean;
  isPreload: boolean;
  width: number;
  height: number;
}) {

  const [paused, setPaused] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
  const { theme } = useThemeSettings();
  const setModalShown = useModalStore((s) => s.setModalShown);
  const shouldRenderIframe = shouldPlay || isPreload;

  const togglePlay = () => {
    if (!iframeRef) return;
  
    if (paused) {
      // Play the video
      iframeRef.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "*"
      );
    } else {
      // Pause the video
      iframeRef.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        "*"
      );
    }
  
    setPaused(!paused);
  };

  const toggleMute = () => {
    if (!iframeRef) return;
  
    if (isMuted) {
      // Unmute the video
      iframeRef.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "unMute", args: [] }),
        "*"
      );
    } else {
      // mute the video
      iframeRef.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "mute", args: [] }),
        "*"
      );
    }
  
    setIsMuted(!isMuted);
  };

  return (
    <View style={{ width, height, overflow: "hidden" } as any}>
      {/* Thumbnail */}
      <img
        src={item.thumbnail}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        alt={item.title}
      />

      {/* YouTube Iframe */}
      {shouldRenderIframe && (
        <iframe
          ref={setIframeRef}
          key={item.videoId}
          src={`https://www.youtube-nocookie.com/embed/${item.videoId}?enablejsapi=1&autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${item.videoId}&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1`}
          style={{
            position: "absolute", top: -55, bottom: 0, left: 0, width: "100%", height: "calc(100% + 130px)", border: "none",
          }}
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}

      {/* Title Bar */}
      <View style={[styles.topTitleBar, { backgroundColor: theme.colors.surfaceColor }]}>
        <Text style={[styles.titleTop, { color: theme.colors.textPrimary }]} numberOfLines={2}>
          {item.title}
        </Text>
      </View>

      {/* Date Badge */}
      <View style={styles.dateBadge}>
        <Text style={styles.dateTopRight}>
          {new Date(item.publishedAt).toLocaleDateString('en-IN', {
            day: '2-digit', month: '2-digit', year: 'numeric'
          })}
        </Text>
      </View>

      {/* Click Overlay */}
      <TouchableOpacity style={styles.clickOverlay} onPress={togglePlay} activeOpacity={1} />

      {/* Pause Button */}
      {paused && (
        <View style={styles.pauseContainer} pointerEvents="none">
          <View style={styles.pauseButton}>
            <Text style={styles.pauseIcon}>▶</Text>
          </View>
        </View>
      )}

      {/* Mute/Unmute Button */}
      <TouchableOpacity style={styles.sideButtons}>
        <View style={[styles.iconCircle, {backgroundColor: theme.colors.btnBgFill,}]}>
          {isMuted ? (
              <CustomIcon icon="mute" isPressable onPress={toggleMute} color={theme.colors.primary}/>

          ) : (
              <CustomIcon icon="unmute" isPressable onPress={toggleMute} color={theme.colors.primary}/>

          )}
        </View>
      </TouchableOpacity>

      {/* Share Button */}
      <View style={styles.sideButtonsForShare}>
        <TouchableOpacity style={styles.sideBtn}>
          <View style={[styles.iconCircle, {backgroundColor: theme.colors.btnBgFill,}]}>
              <CustomIcon
                icon="shareIcon"
                isPressable
                onPress={() => setModalShown(ModalType.SHARE_ARTICLE, { video: item, sharedFrom: ROUTES.WATCH })}
                color={theme.colors.primary}
              />

          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
}


// ---------- Native: react-native-youtube-iframe ----------
/** Only the focused row mounts YoutubePlayer — iOS keeps audio from off-screen WebViews otherwise. */
function NativeShortItem({
  item,
  shouldPlay,
  width,
  height,
}: {
  item: any;
  shouldPlay: boolean;
  width: number;
  height: number;
}) {
  const playerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const setModalShown = useModalStore((s) => s.setModalShown);

  useEffect(() => {
    setReady(false);
  }, [item.videoId, width, height]);
  const showLoader = shouldPlay && !ready;
  const { theme } = useThemeSettings();

  if (!shouldPlay) {
    return (
      <View style={{ width, height, backgroundColor: "#000" }}>
        <Image
          source={{ uri: item.thumbnail }}
          style={StyleSheet.absoluteFillObject}
          resizeMode="cover"
        />
      </View>
    );
  }

  const onShare = () =>
    setModalShown(ModalType.SHARE_ARTICLE, { video: item, sharedFrom: ROUTES.WATCH });

  return (
    <View style={{ width, height }}>
      {showLoader && (
        <View style={styles.nativeLoaderOverlay} pointerEvents="none">
          <AppLoader isLoading={true} />
        </View>
      )}

      {ready && <View style={styles.dateBadgeNative}>
        <Text style={styles.dateTopRight}>
          {new Date(item.publishedAt).toLocaleDateString('en-IN', {
            day: '2-digit', month: '2-digit', year: 'numeric'
          })}
        </Text>
      </View>}
      <View style={styles.nativePlayerContainer}>
        <YoutubePlayer
          ref={playerRef}
          height={height}
          width={width}
          videoId={item.videoId}
          play
          mute={false}
          onReady={() => setReady(true)}
          onChangeState={(event: string) => {
            if (event === "ended") {
              playerRef?.current?.seekTo(0, true);
            }
          }}
          initialPlayerParams={{
            controls: false,
            rel: false,
            modestbranding: true,
            loop: true,
          }}
          webViewProps={{
            injectedJavaScript: `
              var element = document.getElementsByClassName('container')[0];
              if (element) element.style.position = 'unset';
              true;
            `,
            allowsInlineMediaPlayback: true,
            mediaPlaybackRequiresUserAction: false,
            scrollEnabled: false,
          }}
          webViewStyle={[
            styles.nativePlayerWebView,
            !ready && styles.hiddenPlayer,
          ]}
        />
      </View>
      {ready && <View style={styles.sideButtonsNative}>
        <View style={{ backgroundColor: theme.colors.btnBgFill, borderRadius: 30, padding: 10 }}>
          <CustomIcon icon="shareIcon" isPressable onPress={onShare} color={theme.colors.primary}/>
        </View>
      </View>}
    </View>
  );
}

// ---------- ShortItem — ----------
function ShortItem({
  item,
  shouldPlay,
  isPreload,
  width,
  height,
}: {
  item: any;
  shouldPlay: boolean;
  isPreload: boolean;
  width: number;
  height: number;
}) {
  if (Platform.OS === "web") {
    return (
      <WebShortItem
        item={item}
        shouldPlay={shouldPlay}
        isPreload={isPreload}
        width={width}
        height={height}
      />
    );
  }
  return (
    <NativeShortItem
      item={item}
      shouldPlay={shouldPlay}
      width={width}
      height={height}
    />
  );
}

// ---------- Main Screen ----------
export default function WatchScreen() {
  useTrackScreenTime("HOME_WATCH");
  const route = useRoute<any>();
  const sharedVideoId: string | undefined = route.params?.videoId;
  const listRef = useRef<FlatList<any>>(null);
  const didInitialScrollRef = useRef(false);
  const { lastVideoIndex, setLastVideoIndex } = useActiveTabStore();
  const [activeIndex, setActiveIndex] = useState(lastVideoIndex);
  // const insets = useSafeAreaInsets();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [size, setSize] = useState({
    width: Platform.OS === "web" ? 360 : SCREEN.width,
    height: SCREEN.height,
  });
  const [shorts, setShorts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [layoutReady, setLayoutReady] = useState(false);

  // Row height must match the FlatList viewport exactly. Using max(window) inflated rows past
  // the tab/Chrome area so only part of each video fit on screen with gaps/black bars.
  const itemHeight = size.height > 50 ? size.height : windowHeight;
  const itemWidth = size.width > 50 ? size.width : windowWidth;

  useEffect(() => {
    // App already running — just seek, no re-fetch needed
    if (shorts.length && sharedVideoId) {
      const idx = shorts.findIndex((s) => s.videoId === sharedVideoId);
      if (idx >= 0) {
        setActiveIndex(idx);
        setLastVideoIndex(idx);
        listRef.current?.scrollToOffset({ offset: idx * itemHeight, animated: false });
      }
      return;
    }
    fetchShorts()
      .then((res: any) => {
        const data = res.shorts || [];
        setShorts(data);
        if (sharedVideoId) {
          const idx = data.findIndex((s: any) => s.videoId === sharedVideoId);
          if (idx >= 0) {
            setActiveIndex(idx);
            setLastVideoIndex(idx);
          }
        } else {
          setActiveIndex(0);
        }
      })
      .catch((err: any) => console.error("Shorts fetch error:", err))
      .finally(() => setLoading(false));
  }, [i18n.language, sharedVideoId]);

  const viewabilityConfig = useRef({
    viewAreaCoveragePercentThreshold: 60,
    minimumViewTime: 50,
  });

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: { index: number | null; isViewable?: boolean }[] }) => {
      if (!viewableItems?.length) return;
      const visible = viewableItems
        .filter((v) => v.isViewable !== false && v.index != null)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const index = visible[0]?.index;
      if (index != null && typeof index === "number") {
        setActiveIndex(index);
        setLastVideoIndex(index);
      }
    },
    [setLastVideoIndex]
  );

  const syncIndexFromOffset = useCallback(
    (y: number) => {
      const h = itemHeight || 1;
      const idx = Math.round(y / h);
      const max = Math.max(0, shorts.length - 1);
      const clamped = Math.min(Math.max(0, idx), max);
      setActiveIndex(clamped);
      setLastVideoIndex(clamped);
    },
    [itemHeight, shorts.length, setLastVideoIndex]
  );

  const onScroll = useCallback(
    (e: { nativeEvent: { contentOffset: { y: number } } }) => {
      syncIndexFromOffset(e.nativeEvent.contentOffset.y);
    },
    [syncIndexFromOffset]
  );

  const onMomentumScrollEnd = useCallback(
    (e: { nativeEvent: { contentOffset: { y: number } } }) => {
      syncIndexFromOffset(e.nativeEvent.contentOffset.y);
    },
    [syncIndexFromOffset]
  );

  const renderItem = useCallback(
    ({ item, index }: any) => (
      <ShortItem
        item={item}
        shouldPlay={index === activeIndex}
        isPreload={index === activeIndex + 1}
        width={itemWidth}
        height={itemHeight}
      />
    ),
    [activeIndex, itemWidth, itemHeight]
  );

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (width > 50 && height > 50) {
      setSize({
        width,
        height,
      });
      setLayoutReady(true);
    }
  }, []);

  const isReady = !loading && layoutReady;

  const flatListProps = {
    data: shorts,
    renderItem,
    // Rows must re-render when focus changes; otherwise shouldPlay stays stale and audio keeps playing.
    extraData: { activeIndex, itemHeight, itemWidth },
    keyExtractor: (item: any, index: number) => `${item.videoId}-${index}`,
    pagingEnabled: true,
    showsVerticalScrollIndicator: false,
    showsHorizontalScrollIndicator: false,
    snapToInterval: itemHeight,
    snapToAlignment: "start" as const,
    decelerationRate: "fast" as const,
    getItemLayout: (_: any, index: number) => ({
      length: itemHeight,
      offset: itemHeight * index,
      index,
    }),
    viewabilityConfig: viewabilityConfig.current,
    onViewableItemsChanged,
    initialScrollIndex: activeIndex,
    onScroll,
    scrollEventThrottle: 16,
    onMomentumScrollEnd,
    onContentSizeChange: () => {
      if (Platform.OS !== "web") return;
      if (didInitialScrollRef.current || !sharedVideoId) return;
      const idx = shorts.findIndex((s) => s.videoId === sharedVideoId);
      if (idx > 0) {
        listRef.current?.scrollToOffset({ offset: idx * itemHeight, animated: false });
      }
      didInitialScrollRef.current = true;
    },
  };

  return (
    <View style={styles.container} onLayout={onLayout}>
      {!isReady ? (
        <AppLoader isLoading={true} />
      ) : Platform.OS === "web" ? (
        <FlatList
          ref={listRef}
          {...flatListProps}
          style={[styles.flatList, { borderRadius: 12 }]}
        />
      ) : (
        <FlatList
          ref={listRef}
          {...flatListProps}
          style={styles.flatList}
        />
      )}
    </View>
  );
}

// ---------- Styles ----------
const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: "hidden",
    // Stretch so FlatList matches the tab viewport; `center` can shrink the list vs row height.
    alignItems: "stretch",
    alignSelf: "center",
    width: "100%",
    maxWidth: "100%",
    ...(Platform.OS === "web"
      ? {
          width: FULL_WIDTH,
          maxWidth: 425,
        }
      : {}),
  },
  flatList: {
    flex: 1,
    width: "100%",
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: "#000",
    justifyContent: "center",
    alignItems: "center",
  },
  clickOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  },
  pauseContainer: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  pauseButton: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  pauseIcon: {
    color: "#fff",
    fontSize: 28,
    marginLeft: 4,
  },
  channelTitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 13,
    marginBottom: 4,
  },
  titleTop: {
    color: "#221201",
    fontSize: 18,
    fontWeight: "400",
  },
  title: {
    color: "#fff",
    fontSize: 10,
    fontWeight: "400",
  },
  sideBtn: {
    alignItems: "center",
    gap: 4,
  },
  sideButtons: {
    position: "absolute",
    right: 20,
    gap: 30,
    zIndex: 99999,
    bottom: 70,
    paddingRight: 12,
    paddingBottom: 4,
  },
  sideButtonsForShare: {
    position: "absolute",
    right: 20,
    gap: 30,
    zIndex: 99999,
    bottom: 120,
    paddingRight: 12,
    paddingBottom: 4,
  },
  sideButtonsNative: {
    position: "absolute",
    right: 15,
    gap: 30,
    zIndex: 99999,
    bottom: 120,
    paddingRight: 10,
    paddingBottom: 4,
  },
  topTitleBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    padding: 12,
    paddingTop: 14,
    paddingBottom: 14,
    zIndex: 10002,
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  dateBadge: {
    position: "absolute",
    right: 14,
    top: 75,
    zIndex: 10003,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  dateBadgeNative: {
    position: "absolute",
    left: 10,
    bottom: 50,
    zIndex: 10003,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: "rgba(0,0,0,0.20)"
  },
  dateTopRight: {
    color: "#FFFFFF",
    fontSize: 15,
    flexShrink: 0,
  } as any,
  nativeLoaderOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 10004,
    backgroundColor: "#000",
  },
  nativePlayerContainer: {
    flex: 1,
    backgroundColor: "#000",
  },
  nativePlayerWebView: {
    backgroundColor: "#000",
  },
  hiddenPlayer: {
    opacity: 0,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 9999,
    justifyContent: "center",
    alignItems: "center",
  } as any,
});